import React from 'react';
import { useWishlist } from './WishlistContext';

const ProductCard = ({
  product,
  getImageUrl,
  formatPrice,
  onClick,
  onShare,
  showDiscount = false,
}) => {
  const wishlist = useWishlist();
  if (!product) return null;

  const price = Number(product.price || 0);
  const originalPrice = Number(product.original_price || 0);
  const hasRealDeal = Boolean(
    product.is_flash_deal && originalPrice > price
  );
  const discount = hasRealDeal
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;

  // `showDiscount` is retained for API compatibility, but discounts are never
  // fabricated. A marketplace card must only show claims backed by data.
  void showDiscount;

  const inWishlist = wishlist.isInWishlist(product.id);
  const isAvailable = product.available !== false;

  return (
    <article
      data-testid={`product-card-${product.id || product.name}`}
      className="group bg-white border border-gray-200 rounded-3xl overflow-hidden hover:border-cyan-500 hover:shadow-lg active:scale-[0.99] transition-all duration-200 text-left w-full"
    >
      <div className="relative aspect-square bg-gray-50 overflow-hidden">
        {discount > 0 && (
          <span className="absolute top-2 left-2 z-10 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{discount}%
          </span>
        )}

        <div className="absolute top-2 right-2 z-10 flex gap-1.5">
          {onShare && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onShare(product, e);
              }}
              data-testid={`product-share-${product.id || product.name}`}
              className="w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-white/95 backdrop-blur text-gray-500 hover:text-cyan-700 active:scale-95 flex items-center justify-center transition-all shadow-sm touch-manipulation"
              aria-label="Share product"
            >
              <i className="fa-solid fa-share-nodes text-xs" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              wishlist.toggleWishlist(product);
            }}
            data-testid={`product-wishlist-${product.id || product.name}`}
            className={`w-10 h-10 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all shadow-sm active:scale-95 touch-manipulation ${
              inWishlist
                ? 'bg-red-500 text-white'
                : 'bg-white/95 backdrop-blur text-gray-500 hover:text-red-500'
            }`}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
          >
            <i className={`${inWishlist ? 'fa-solid' : 'fa-regular'} fa-heart text-xs`} />
          </button>
        </div>

        <button
          type="button"
          onClick={() => isAvailable && onClick?.(product)}
          className={`w-full h-full block ${isAvailable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          aria-label={`View ${product.name}`}
        >
          {!isAvailable && (
            <span className="absolute inset-0 z-10 bg-black/45 flex items-center justify-center text-white text-xs font-bold">
              Currently unavailable
            </span>
          )}

          <img
            src={getImageUrl ? getImageUrl(product.image_url) : product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
                    decoding="async"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://via.placeholder.com/300?text=Product';
            }}
          />
        </button>
      </div>

      <div className="p-3 sm:p-3.5">
        <button
          type="button"
          onClick={() => isAvailable && onClick?.(product)}
          className={`w-full text-left ${isAvailable ? '' : 'cursor-not-allowed'}`}
        >
          <p className="text-xs sm:text-sm font-bold text-gray-900 line-clamp-2 leading-snug min-h-[2.35rem]">
            {product.name}
          </p>
        </button>

        {product.vendor_name && (
          <p className="text-[11px] text-gray-400 mt-1 line-clamp-1">
            {product.vendor_name}
          </p>
        )}

        <div className="mt-2 flex items-baseline gap-1.5 flex-wrap">
          <span className="text-base sm:text-lg font-extrabold text-red-600">
            {formatPrice ? formatPrice(price) : `₦${price.toLocaleString()}`}
          </span>
          {hasRealDeal && (
            <span className="text-xs text-gray-400 line-through">
              {formatPrice ? formatPrice(originalPrice) : `₦${originalPrice.toLocaleString()}`}
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export default ProductCard;




